import {takeUntil} from 'rxjs/operators';
/**
 * The ModalToolContainer provides an environment where webmate tools can be displayed independently from any pages.
 * This component is responsible for:
 * - tool nesting via breadcrumbs
 */
import {ApplicationRef, ChangeDetectorRef, Component, ComponentFactoryResolver, InjectionToken, Injector} from '@angular/core';
import {ComponentPortal, DomPortalOutlet} from '@angular/cdk/portal';
import {ModalToolContainerService, WebmateToolComponent, WebmateToolConfig} from './modaltoolcontainer.service';
import {Subject} from 'rxjs';
import _ from 'lodash';

export const TOOL_CONTAINER_DATA = new InjectionToken<any>('TOOL_CONTAINER_DATA');

@Component({
    selector: 'wm-modaltoolcontainer',
    templateUrl: './modaltoolcontainer.component.html',
    styleUrls: ['./modaltoolcontainer.component.less']
})
export class ModalToolContainerComponent {

    private static _MODALTOOLCONTAINERVIEW_IDPREFIX = '#wm-modaltoolcontainer-view';

    private portalHosts: DomPortalOutlet[] = [];

    private portals: ComponentPortal<any>[] = [];


    currentToolChain: Array<[WebmateToolComponent<any>, WebmateToolConfig]> = [];

    modalToolContainerIsDisplayed: boolean;

    toolContainerClosed$ = new Subject<any>();


    constructor(private componentFactoryResolver: ComponentFactoryResolver,
                private injector: Injector,
                private appRef: ApplicationRef,
                private changeDetectorRef: ChangeDetectorRef,
                private modalToolService: ModalToolContainerService) {
        this.modalToolService.toolContainerDisplayed().subscribe(isDisplayed => {
            if (isDisplayed && !this.modalToolContainerIsDisplayed) {
                this.modalToolContainerIsDisplayed = true;
                setTimeout(() => {
                    // Ensure the conditional outlet elements exist before creating their portal hosts.
                    this.changeDetectorRef.detectChanges();
                    this.renderToolContainer();
                });
            } else if (!isDisplayed) {
                this.modalToolContainerIsDisplayed = false;
                this.currentToolChain = [];
                this.portals.forEach((_, i) => this.disposePortalAtIndex(i));
                this.portalHosts.forEach(h => h.dispose());  // destroys outlet + componen

                this.portals = [];
                this.portalHosts = [];
                this.toolContainerClosed$.next('closed');
            }
        });
    }

    shouldScroll(idx: number) {
        return this.currentToolChain?.[idx]?.[1].scrollable;
    }

    currentToolConfig(): WebmateToolConfig | null {
        return this.currentToolChain && this.currentToolChain.length > 0 ? this.currentToolChain[this.currentToolChain.length - 1][1] : null;
    }

    previousToolConfig(): WebmateToolConfig | null {
        return this.currentToolChain?.[this.currentToolChain.length - 2]?.[1];
    }

    currentIndex(): number {
        return this.currentToolChain?.length - 1;
    }

    private _makePortalOutletForIndex(index: number): DomPortalOutlet {
        return new DomPortalOutlet(document.querySelector(ModalToolContainerComponent._MODALTOOLCONTAINERVIEW_IDPREFIX + index)!,
            this.componentFactoryResolver, this.appRef, this.injector);
    }

    renderToolContainer() {
        this.portalHosts = [
            this._makePortalOutletForIndex(0),
            this._makePortalOutletForIndex(1),
            this._makePortalOutletForIndex(2),
            this._makePortalOutletForIndex(3),
            this._makePortalOutletForIndex(4)
        ];

        // looks hacky but is the officially recommended way to do something after all children have been fully rendered
        setTimeout(() => {
            this.modalToolService.toolChain$().pipe(takeUntil(this.toolContainerClosed$)).subscribe(toolChainUpdate => {
                if (toolChainUpdate.length > 5) {
                    console.error("Not good. More than 5 tools in modaltoolcontainer. ignoring request.");
                } else if (toolChainUpdate.length < 1) {
                    this.currentToolChain = [];
                } else {
                    const previousToolChain = this.currentToolChain;
                    this.currentToolChain = toolChainUpdate;

                    this.currentToolChain.forEach((currentTool, index) => {
                        const previousTool = previousToolChain ? previousToolChain[index] : null;
                        if (previousTool && _.isEqual(previousTool, currentTool)) {
                            return;
                        } else {
                            this.disposePortalAtIndex(index);

                            const injector = Injector.create({
                                parent: this.injector,
                                providers: [
                                    {provide: TOOL_CONTAINER_DATA, useValue: currentTool[1]}
                                ]
                            });
                            const portal = new ComponentPortal<any>(currentTool[0], null, injector);
                            this.portalHosts[this.currentIndex()].attachComponentPortal(portal);
                            this.portals[this.currentIndex()] = portal;
                        }
                    });

                    // remove all portals that are not in the current tool chain
                    for (let i = this.currentToolChain.length; i < this.portalHosts.length; i++) {
                        this.disposePortalAtIndex(i);
                        this.portalHosts[i] = this._makePortalOutletForIndex(i);
                    }
                }
            });
        });
    }

    closeToolContainer() {
        if (this.currentToolConfig()?.notDismissible) {
            return;
        }
        this.modalToolService.disposeToolContainer();
    }

    /**
     * Removes the portal at portaloutlet with index.
     *
     * It seems, that there is no way to achieve this using PortalOutlet functionality, at the moment.
     */
    disposePortalAtIndex(index: number) {
        const elem = document.querySelector(ModalToolContainerComponent._MODALTOOLCONTAINERVIEW_IDPREFIX + index);
        if (elem?.firstChild) {
            elem.removeChild(elem.firstChild);
        }
    }

    closeCurrentTool() {
        this.detachLastPortal();
        this.modalToolService.closeLastTool();
    }

    detachLastPortal() {
        this.portals.pop();
        this.disposePortalAtIndex(this.currentIndex());
        this.portalHosts[this.currentIndex()].detach();
        this.portalHosts[this.currentIndex()] = this._makePortalOutletForIndex(this.currentIndex());
    }
}
